import { isNil, isNumber } from 'lodash';
import { DateTime } from 'luxon';
import type {
  Ranges,
  ResultHistory,
} from '../../../../../../../services/MedicalResultsService';
import { isDocumentExtractionV2Result } from '../../../../../../../services/MedicalResultsService';
import { getColorByStatus } from '../../../util/graphUtils';

type ChartData = {
  scatterChartData: ScatterChartData[];
  // areaCharts: Record<keyof Ranges, AreaScatterChartData[]>;
  metadata: Metadata;
};

export type ScatterChartData = {
  x: number;
  y: number | null;
  valueDisplay?: string;
  color: string;
  delta?: number;
  unit?: string;
  dateTime?: DateTime;
  pending: boolean;
  extracted: boolean;
};

type AreaScatterChartData =
  | {
      x: number;
      y: number;
      y0: number | null;
    }
  | {
      x: number;
      y: null;
    };

type Metadata = {
  minYValue: { value: number; valueDisplay?: string } | null;
  maxYValue: { value: number; valueDisplay?: string } | null;
  range: number | null;
};

export const COMPARATORS = ['>', '<', '>=', '<=', 'ad'] as const;

export type Comparator = (typeof COMPARATORS)[number];

export function intoChartData(resultHistories: ResultHistory[]): ChartData {
  const sortedResultHistories = resultHistories.sort((a, b) => {
    const dateA =
      a.resource.effectiveDateTime ?? a.resource.effectivePeriod?.end;
    const dateB =
      b.resource.effectiveDateTime ?? b.resource.effectivePeriod?.end;
    if (!dateA && !dateB) return 0;
    if (!dateA) return -1;
    if (!dateB) return 1;

    return (
      DateTime.fromISO(dateA).toMillis() - DateTime.fromISO(dateB).toMillis()
    );
  });

  const scatterChartData = intoScatterChartData(sortedResultHistories);
  const metadata = intoMetadata(sortedResultHistories);
  // temporarily disabled as they're unused
  // const inRangeAreaScatterChartData = intoAreaScatterChartData(
  //   sortedResultHistories,
  //   'in-range',
  // );
  // const attentionAreaScatterChartData = intoAreaScatterChartData(
  //   sortedResultHistories,
  //   'attention',
  // );
  // const optimalAreaScatterChartData = intoAreaScatterChartData(
  //   sortedResultHistories,
  //   'optimal',
  // );

  return {
    scatterChartData,
    // areaCharts: {
    //   'in-range': inRangeAreaScatterChartData,
    //   attention: attentionAreaScatterChartData,
    //   optimal: optimalAreaScatterChartData,
    // },
    metadata,
  };
}

export function intoScatterChartData(
  resultHistories: ResultHistory[],
): ScatterChartData[] {
  const scatterChartData: ScatterChartData[] = [];

  let datumIndex = 0;
  for (const resultHistory of resultHistories.values()) {
    const res =
      intoObservationValue(resultHistory) ??
      ({
        value: null,
      } as { value: null; valueDisplay?: string });
    let value = res.value;
    const valueDisplay = res.valueDisplay;
    const color = getColorByStatus(resultHistory.result.status);
    const previousValue = scatterChartData.at(-1)?.y ?? null;
    const unit =
      resultHistory.resource.valueQuantity?.unit ||
      resultHistory.resource.component?.[0]?.valueQuantity?.unit;
    const dateTime = DateTime.fromISO(
      resultHistory.resource.effectiveDateTime ??
        resultHistory.resource.effectivePeriod?.end ??
        resultHistory.resource.effectivePeriod?.start ??
        '',
    );

    let delta =
      value != null && previousValue != null
        ? value - previousValue
        : undefined;

    if (delta) {
      delta = Math.round(delta * 100) / 100;
    }

    if (value == null) {
      if (
        (resultHistory.resource.status as string) === 'pending' &&
        previousValue != null
      ) {
        // show a pending value as previous value with pending indication
        value = previousValue;
      } else {
        continue; // otherwise, we can't render this point
      }
    }

    scatterChartData.push({
      x: datumIndex++,
      y: value,
      valueDisplay,
      color,
      delta,
      unit,
      dateTime: dateTime.isValid ? dateTime : undefined,
      pending: (resultHistory.resource.status as string) === 'pending',
      extracted: isDocumentExtractionV2Result(resultHistory),
    });
  }

  return scatterChartData;
}

type ValueGetterResult = {
  value: number;
  valueAsString: string;
  unit?: string;
};

export const valueGetters = {
  getValueQuantity(valueQuantity?: fhir.Quantity): ValueGetterResult | null {
    if (!valueQuantity || isNil(valueQuantity.value)) return null;
    const value = getNumber(valueQuantity.value);
    if (value === null) return null;
    return {
      value,
      valueAsString: valueQuantity.comparator
        ? `${valueQuantity.comparator} ${value}`
        : value.toString(),
      unit: valueQuantity.unit,
    };
  },

  getValueString(valueString?: string): ValueGetterResult | null {
    if (!valueString) return null;
    valueString = valueString.trim();

    for (const comp of COMPARATORS) {
      if (valueString.startsWith(comp)) {
        const numericPart = valueString.substring(comp.length).trim();
        const value = getNumber(numericPart);
        if (value === null) return null;
        return { value, valueAsString: valueString };
      }
    }

    if (valueString.includes(':')) {
      const [numerator, denominator] = valueString.split(':').map(getNumber);
      if (numerator === null || denominator === null) return null;
      const value = getNumber(numerator / denominator);
      if (value === null) return null;
      return { value, valueAsString: valueString };
    }

    const value = getNumber(valueString);
    if (value === null) return null;
    return { value, valueAsString: valueString };
  },
};

export function intoObservationValue(result: ResultHistory):
  | {
      value: number;
      valueDisplay?: string;
    }
  | undefined {
  const observation = result.resource;

  const valueQuantities = [
    observation.valueQuantity,
    ...(observation.component?.map(c => c.valueQuantity) ?? []),
  ];

  let v: ValueGetterResult | null = null;
  for (const valueQuantity of valueQuantities) {
    v = valueGetters.getValueQuantity(valueQuantity);
    if (v)
      return {
        value: v.value,
        valueDisplay: v.valueAsString,
      };
  }

  const valueStrings = [
    observation.valueString,
    ...(observation.component?.map(c => c.valueString) ?? []),
  ];

  for (const valueString of valueStrings) {
    v = valueGetters.getValueString(valueString);
    if (v)
      return {
        value: v.value,
        valueDisplay: v.valueAsString,
      };
  }

  return undefined;
}

export function intoAreaScatterChartData(
  resultHistories: ResultHistory[],
  rangeType: keyof Ranges,
): AreaScatterChartData[] {
  const areaScatterChartData: AreaScatterChartData[] = [];

  for (const [index, resultHistory] of resultHistories.entries()) {
    const ranges =
      resultHistory.type === 'withRange' ? resultHistory.ranges : undefined;
    const range = ranges?.[rangeType];

    if (!range) {
      areaScatterChartData.push({
        x: index,
        y: null,
      });
      continue;
    }

    areaScatterChartData.push({
      x: index,
      y: range.upper ?? Infinity,
      y0: range.lower ?? -Infinity,
    });
  }

  return areaScatterChartData;
}

export function intoMetadata(resultHistories: ResultHistory[]): Metadata {
  let maxYValue: { value: number; valueDisplay?: string } | null = null;
  let minYValue: { value: number; valueDisplay?: string } | null = null;
  let range: number | null = null;

  for (const resultHistory of resultHistories) {
    const { value, valueDisplay } =
      intoObservationValue(resultHistory) ??
      ({
        value: null,
      } as { value: null; valueDisplay?: string });

    if (value !== null) {
      const rawMaxYValue = Math.max(maxYValue?.value ?? -Infinity, value);
      const rawMinYValue = Math.min(minYValue?.value ?? Infinity, value);
      if (maxYValue === null || rawMaxYValue !== maxYValue?.value) {
        maxYValue = { value: rawMaxYValue, valueDisplay: valueDisplay };
      }
      if (minYValue === null || rawMinYValue !== minYValue?.value) {
        minYValue = { value: rawMinYValue, valueDisplay: valueDisplay };
      }
    }
  }

  if (minYValue !== null && maxYValue !== null) {
    range = maxYValue.value - minYValue.value;
  }

  return { minYValue, maxYValue, range };
}

export function getNumber(n: number | string): number | null {
  n = isNumber(n) ? n : parseFloat(n);
  if (isNaN(n)) return null;
  return parseFloat(n.toPrecision(4));
}

export function shouldUseCategoryGraph(
  resultHistories: ResultHistory[],
): boolean {
  return resultHistories.every(resultHistory => {
    return (resultHistory.resource.component?.length ?? 0) > 1;
  });
}
